import type { GameBridge } from './bridge';
import type { PauseCoordinator, PauseLease } from './pause-coordinator';

/**
 * Pauses the hunt whenever the tab is not visible.
 *
 * A hidden tab must grant exactly zero progress. Browsers already throttle or
 * stop `requestAnimationFrame` in background tabs, but that behaviour is not
 * guaranteed and the frame delivered on return carries the whole hidden gap -
 * so the pause is made explicit here and the resume delta is discarded by
 * `HuntClock` on the simulation side.
 *
 * Listeners:
 * - `visibilitychange` - the primary signal (tab switch, window minimise).
 * - `pagehide` - fires on bfcache eviction / navigation where
 *   `visibilitychange` may not; always forces the paused state.
 * - `pageshow` - restoring from bfcache; re-publishes the real state.
 * - `game:ready` - see below.
 *
 * ## Why `game:ready` matters
 *
 * React mounts and attaches this while the asset manifest is still loading, so
 * the immediate `publish()` happens BEFORE the Phaser scene has subscribed to
 * `ui:set-paused`. The bridge is a plain pub/sub with no replay - deliberately,
 * because a queue would also redeliver stale commands - so that first message
 * reaches nobody. A page opened in a background tab would therefore start with
 * `HuntClock` unpaused and quietly simulate.
 *
 * The scene registers its bridge listeners before emitting `game:ready`, so
 * republishing on that event closes the window without buffering anything.
 *
 * Returns a disposer that removes every listener. Registering twice would
 * duplicate messages, so the caller owns exactly one subscription.
 */
export function attachVisibilityPause(
  bridge: GameBridge,
  coordinator: PauseCoordinator,
  doc: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>,
  win: Pick<Window, 'addEventListener' | 'removeEventListener'>,
): () => void {
  /*
   * This module no longer publishes.
   *
   * It owns ONE lease and says whether it is held; the coordinator ORs it with
   * every other reason and is the only thing that touches the bridge. That is
   * the whole fix for the conflict this file used to cause: coming back to a
   * visible tab now drops a lease rather than announcing `paused: false` over
   * a duel that is still holding its own.
   */
  let lease: PauseLease | null = null;

  const hold = (): void => {
    lease ??= coordinator.acquire('visibility');
  };
  const drop = (): void => {
    lease?.();
    lease = null;
  };
  const follow = (): void => {
    if (doc.visibilityState === 'visible') {
      drop();
    } else {
      hold();
    }
  };

  /*
   * Take the current state immediately: the tab may already be hidden when the
   * app mounts (opened in a background tab).
   *
   * This no longer PUBLISHES by itself. The coordinator says something only
   * when the aggregate actually changes, so attaching in a hidden tab still
   * pauses, and attaching in a visible tab is silent - which is right, because
   * the message it used to send reached nobody anyway (the scene has not
   * subscribed yet) and `game:ready` is what closes that window.
   */
  follow();

  doc.addEventListener('visibilitychange', follow);
  win.addEventListener('pagehide', hold);
  win.addEventListener('pageshow', follow);
  /*
   * Re-send the aggregate once the scene is listening.
   *
   * React attaches this while the asset manifest is still loading, so the
   * first publication happens before the scene subscribed, and the bridge has
   * no replay. Republishing the AGGREGATE - not this module's own opinion - is
   * what keeps a duel's lease from being talked over here.
   */
  const offGameReady = bridge.onUiMessage('game:ready', () => {
    coordinator.republish();
  });

  return () => {
    doc.removeEventListener('visibilitychange', follow);
    win.removeEventListener('pagehide', hold);
    win.removeEventListener('pageshow', follow);
    offGameReady();
    // Only ever this module's own lease. Detaching must not resume a hunt that
    // a duel is still holding paused.
    drop();
  };
}
